import React from 'react';
import { observer, inject } from 'mobx-react';
import styled from 'styled-components';

import { Box, Text, Heading, Stack } from 'grommet';
import {
  Menu,
  Search,
  Bookmark,
  Revert,
  Close,
  More,
  Edit,
  Checkmark,
  ChapterNext,
  ChapterPrevious,
  Trash,
  Clear,
  Iteration,
  Connect,
  Globe,
} from 'grommet-icons';
import {
  Table as SuperResponsiveTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from 'react-super-responsive-table';

import BookElement from 'app/api/bookElement';

import Paragraph from 'app/components/Paragraph';
import SectionBreak from 'app/components/BookElements/SectionBreak';
import TableOfContentsEntry from 'app/components/BookElements/TableOfContentsEntry';

const ResponsiveTable = styled(SuperResponsiveTable)`
  border-collapse: collapse;
  border-style: hidden;
  td,
  th {
    border: 1px solid gray;
    padding: 5px;
  }
`;

const ClearIcon = styled(Clear)`
  transform: translateY(2px);
  width: 28px;
  height: 28px;
`;
const BookmarksIcon = styled(Iteration)`
  transform: translateY(0px);
  width: 14px;
  height: 14px;
`;

class Instructions extends React.Component {
  render() {
    const { path } = this.props;
    if (BookElement.isNotSelected(path)) return null;

    return (
      <React.Fragment>
        <TableOfContentsEntry path={path}>
          <Heading responsive textAlign="center">
            Instructions
          </Heading>
        </TableOfContentsEntry>
        <Text as="h3" alignSelf="start" size="large" weight="bold">
          Control Icons
        </Text>
        <Paragraph>
          Control icons are located along the bottom in the menu bar. Most of
          these controls are intuitive. These controls and their functions are
          described in below.
        </Paragraph>
        <Box background="light-3" elevation="small">
          <ResponsiveTable border={1}>
            <Thead>
              <Tr>
                <Th>Icon</Th>
                <Th>Function</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Menu color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Table of Contents: Select the icon to access the table of
                    contents and select a heading from the scrollable list
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Search color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Search: Enter a word or group of words to locate the search
                    word(s) within the entire document
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <ChapterPrevious color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Previous Section: Use to navigate to the previous section
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <ChapterNext color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Next Section: Use to navigate to the next section
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <More color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Bookmark Menu: Select the icon to access a scrollable list
                    of captured bookmarks
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Bookmark color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Bookmark: Use to mark a paragraph or sentence for quick
                    retrieval in the future
                  </Text>
                </Td>
              </Tr>

              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Edit color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Annotate Bookmark: Use to add a note to a bookmark
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Trash color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Delete Bookmark: Use to select a bookmark to delete
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Stack anchor="center">
                      <ClearIcon size="large" color="black" />
                      <BookmarksIcon color="black" />
                    </Stack>
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Clear Bookmarks: Use to delete bookmarks all at once
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Checkmark color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Confirm: Use to confirm an action, such as creating or
                    deleting a bookmark
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Close color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Cancel: Use to cancel an action
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Revert color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Revert: Use to back out of a menu or back up to a previous
                    step
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Globe color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Online: This icon indicates the e2book has access to the
                    internet
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Box align="center" justify="center">
                    <Connect color="black" />
                  </Box>
                </Td>
                <Td>
                  <Text as="h3" alignSelf="start" size="small" weight="normal">
                    Offline: This icon indicates the e2book lost internet
                    access, and that some figure types may be inaccessible
                  </Text>
                </Td>
              </Tr>
            </Tbody>
          </ResponsiveTable>
        </Box>
        <Text as="h3" alignSelf="start" size="large" weight="bold">
          Navigation
        </Text>
        <Paragraph>
          There are several ways to navigate through the text. Select Next Page
          or Previous Page navigation bar located at the bottom of the page,
          respectively to advance or go back one page. To jump to a particular
          chapter or topic, select the table of contents icon, then select the
          desired heading from the list. You can also enter a search term and
          select a page from the list matches that appear in the table of
          contents pane. Use the scroll bar to move up and down the page.
        </Paragraph>
        <Text as="h3" alignSelf="start" size="large" weight="bold">
          Figure Types
        </Text>
        <Paragraph>
          Figure types may represent audio/video files, animations, or
          interactions. Some of the figure types are expandable to full screen.
          To return to the page, click the contract button that appears in the
          upper left corner of the full screen.
        </Paragraph>
        <Text as="h3" alignSelf="start" size="large" weight="bold">
          “Missing” Text and Prompts
        </Text>
        <Paragraph>
          In some instances, the text may appear to be missing. This indicates
          that the text has been incorporated into figure type. This is denoted
          by the numbered paragraph and the prompt, “Select/See the figure…” or
          “Select/See supplemental figure…” and a related image. You must select
          the related image in order to view the text.
        </Paragraph>
        <Text as="h3" alignSelf="start" size="large" weight="bold">
          Notes
        </Text>
        <Paragraph>
          A notes feature is provided to allow linking comments to a specific
          location within the IDP. To capture a note, first select the Bookmark
          icon (as seen above in the Control Icons table), then select a section
          to highlight it. At this point, you can either select individual
          sentences in the section, add a note, or just save the selection as a
          bookmark.
        </Paragraph>
        <SectionBreak />
      </React.Fragment>
    );
  }
}

const Component = inject('AppState')(observer(Instructions));
Component.displayName = 'Instructions';
export const InstructionsComponent = Instructions;
export default Component;



// WEBPACK FOOTER //
// ./src/app/components/BookElements/Instructions.js